import { useState, useEffect } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Loader2 } from 'lucide-react'
import { SimpleIcon } from '@adthlete/common'
import { handleYouTubeCallback } from '@/lib/api'

const YouTubeCallback = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const code = queryParams.get('code')
    const state = queryParams.get('state')
    const errorParam = queryParams.get('error')
    const errorDescription = queryParams.get('error_description')

    const processYouTubeCallback = async () => {
      if (errorParam) {
        console.error('YouTube authorization error:', errorParam, errorDescription)
        setError(errorDescription || 'An error occurred during YouTube authorization')
        setLoading(false)
        return
      }

      if (!code) {
        setError('No authorization code received from Google')
        setLoading(false)
        return
      }

      try {
        // Navigate to auth steps page with the callback data
        const params = new URLSearchParams()
        if (code) params.append('code', code)
        if (state) params.append('state', state)

        navigate(`/youtube/auth-steps?${params.toString()}`)
      } catch (err: any) {
        console.error('Error processing YouTube callback:', err)
        setError('An error occurred while processing your request')
        setLoading(false)
      }
    }

    processYouTubeCallback()
  }, [location.search, navigate])

  if (loading) {
    return (
      <div className="w-full max-w-2xl mx-auto pb-4 min-h-screen flex items-center justify-center">
        <Card variant="dark" className="overflow-hidden">
          <CardContent className="p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-red-400 p-4 rounded-full">
                <SimpleIcon slug="youtube" color="white" height={32} width={32} />
              </div>
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">
              Processing YouTube Authorization
            </h2>
            <p className="text-gray-600 mb-4">
              Please wait while we connect your YouTube channel...
            </p>
            <Loader2 className="h-6 w-6 animate-spin text-[#4d7c0f] mx-auto" />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full max-w-2xl mx-auto pb-4 min-h-screen flex items-center justify-center">
        <Card variant="dark" className="overflow-hidden">
          <CardContent className="p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-red-400 p-4 rounded-full">
                <SimpleIcon slug="youtube" color="white" height={32} width={32} />
              </div>
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">YouTube Authorization Error</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="space-x-4">
              <Link
                to="/connect/youtube"
                className="inline-block bg-[#C0FD72] text-black px-4 py-2 rounded-lg font-medium hover:bg-[#d2ff9c] transition-colors"
              >
                Try Again
              </Link>
              <Link
                to="/settings"
                className="inline-block text-gray-600 hover:text-foreground px-4 py-2 transition-colors"
              >
                Back to Settings
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return null
}

export default YouTubeCallback
